export const de = {
  policy: {
    title: 'Platform Policy',
    subtitle: 'Compliance und Richtlinien',
    platformCardTitle: 'Plattform-Richtlinie',
    platformEmpty: 'Keine OpenDeskPlatformPolicy gefunden. Erstellen Sie eine, um Plattform-Limits zu definieren.',
    kpi: {
      tenants: 'Mandanten',
      noLimit: 'kein Limit',
      maxPodsPerTenant: 'Max Pods/Mandant',
      maxStoragePerTenant: 'Max Storage/Mandant',
      defaultEdition: 'Default Edition',
      allowedEditions: 'Erlaubte Editionen',
      allowedEditionsAll: 'alle',
      maintenanceWindow: 'Wartungsfenster (UTC)',
      maintenanceWindowUnset: 'nicht gesetzt',
    },
    profilesCardTitle: 'Compliance-Profile',
    profilesEmpty: 'Keine OpenDeskPolicyProfile CRs gefunden. Erstellen Sie ein Profil, um Compliance zu überwachen.',
    table: {
      profile: 'Profil',
      mode: 'Modus',
      score: 'Score',
      violations: 'Verstöße',
      lastScan: 'Letzter Scan',
    },
  },
  policyDetail: {
    breadcrumb: 'Policy',
    subtitlePrefix: 'Compliance-Details für Profil',
    subtitleDefault: 'Compliance-Details für das gewählte Profil',
    noProfileInUrl: 'Kein Profilname in der URL angegeben.',
    noDetailFound: 'Kein PolicyDetail für dieses Profil gefunden.',
    summary: {
      cardTitle: 'Zusammenfassung',
      profile: 'Profil',
      mode: 'Modus',
      score: 'Score',
      violations: 'Verstöße',
      lastScanPrefix: 'Letzter Scan:',
    },
    controls: {
      cardTitle: 'Kontrollen',
      cardTitleWithTotalPrefix: 'Kontrollen (',
      cardTitleWithTotalSuffix: ' gesamt)',
      empty: 'Keine Kontrollen für dieses Profil hinterlegt.',
      satisfiedSuffix: 'erfüllt',
      statusHeader: 'Status',
      control: 'Kontrolle',
      titleHeader: 'Titel',
      failingChecks: 'Fehlgeschlagene Checks',
      satisfiedTitle: 'Erfüllt',
      violationTitle: 'Verstoß',
      unverifiedTitle: 'Nicht geprüft: Verschlüsselung von Objektspeicher und Volumes wird noch nicht geprüft',
    },
    violations: {
      cardTitleWithCountPrefix: 'Verstöße (',
      cardTitleWithCountSuffix: ')',
      empty: 'Keine Verstöße. Alle aktiven Checks sind erfüllt.',
      check: 'Check',
      resource: 'Ressource',
      message: 'Meldung',
      timestamp: 'Zeitpunkt',
      checkDisabled: 'check disabled',
    },
  },
  audit: {
    title: 'Audit-Log',
    subtitle: 'BSI OPS.1.1.5 Protokollierung: Operator-Aktionen mit Framework-Kontrollen',
    filters: {
      cardTitle: 'Filter',
      action: 'Aktion',
      actionAll: '(alle)',
      actionAriaLabel: 'Audit-Aktion filtern',
      actor: 'Akteur',
      actorPlaceholder: 'z. B. opendesktenant-controller',
      actorAriaLabel: 'Akteur filtern',
      resource: 'Ressource (Kind)',
      resourcePlaceholder: 'z. B. OpenDeskTenant',
      resourceAriaLabel: 'Ressourcentyp filtern',
      tenant: 'Mandant (Namespace)',
      tenantPlaceholder: 'z. B. opendesk-op-tenant',
      tenantAriaLabel: 'Mandant filtern',
      from: 'Von',
      fromAriaLabel: 'Startzeit',
      to: 'Bis',
      toAriaLabel: 'Endzeit',
      limit: 'Limit',
      limitAriaLabel: 'Maximale Zeilen',
    },
    entries: {
      cardTitle: 'Audit-Einträge',
      empty: 'Keine Audit-Ereignisse im gewählten Zeitraum. Operator-Aktionen werden in einem an BSI OPS.1.1.5 angelehnten Format erfasst und über Kubernetes Events persistiert.',
      loadMore: 'Weitere laden',
      loading: 'Lade…',
      inNamespacePrefix: 'in',
      summarySuccess: 'success',
      summaryFailure: 'failure',
      summarySkipped: 'skipped',
    },
    table: {
      timestamp: 'Zeitpunkt',
      actor: 'Akteur',
      action: 'Aktion',
      resource: 'Ressource',
      outcome: 'Ergebnis',
      framework: 'Framework',
      details: 'Details',
    },
    detail: {
      detail: 'Detail',
      bsiRef: 'BSI-Referenz',
      frameworkRefs: 'Framework-Referenzen',
    },
  },
} as const;

export const en = {
  policy: {
    title: 'Platform Policy',
    subtitle: 'Compliance and policies',
    platformCardTitle: 'Platform Policy',
    platformEmpty: 'No OpenDeskPlatformPolicy found. Create one to define platform limits.',
    kpi: {
      tenants: 'Tenants',
      noLimit: 'no limit',
      maxPodsPerTenant: 'Max pods/tenant',
      maxStoragePerTenant: 'Max storage/tenant',
      defaultEdition: 'Default edition',
      allowedEditions: 'Allowed editions',
      allowedEditionsAll: 'all',
      maintenanceWindow: 'Maintenance window (UTC)',
      maintenanceWindowUnset: 'not set',
    },
    profilesCardTitle: 'Compliance profiles',
    profilesEmpty: 'No OpenDeskPolicyProfile CRs found. Create a profile to monitor compliance.',
    table: {
      profile: 'Profile',
      mode: 'Mode',
      score: 'Score',
      violations: 'Violations',
      lastScan: 'Last scan',
    },
  },
  policyDetail: {
    breadcrumb: 'Policy',
    subtitlePrefix: 'Compliance details for profile',
    subtitleDefault: 'Compliance details for the selected profile',
    noProfileInUrl: 'No profile name given in the URL.',
    noDetailFound: 'No policy detail found for this profile.',
    summary: {
      cardTitle: 'Summary',
      profile: 'Profile',
      mode: 'Mode',
      score: 'Score',
      violations: 'Violations',
      lastScanPrefix: 'Last scan:',
    },
    controls: {
      cardTitle: 'Controls',
      cardTitleWithTotalPrefix: 'Controls (',
      cardTitleWithTotalSuffix: ' total)',
      empty: 'No controls defined for this profile.',
      satisfiedSuffix: 'satisfied',
      statusHeader: 'Status',
      control: 'Control',
      titleHeader: 'Title',
      failingChecks: 'Failing checks',
      satisfiedTitle: 'Satisfied',
      violationTitle: 'Violation',
      unverifiedTitle: 'Not verified: object storage and volume encryption are not checked yet',
    },
    violations: {
      cardTitleWithCountPrefix: 'Violations (',
      cardTitleWithCountSuffix: ')',
      empty: 'No violations, all active checks pass.',
      check: 'Check',
      resource: 'Resource',
      message: 'Message',
      timestamp: 'Timestamp',
      checkDisabled: 'check disabled',
    },
  },
  audit: {
    title: 'Audit log',
    subtitle: 'BSI OPS.1.1.5 logging: operator actions with framework controls',
    filters: {
      cardTitle: 'Filters',
      action: 'Action',
      actionAll: 'all',
      actionAriaLabel: 'Filter by audit action',
      actor: 'Actor',
      actorPlaceholder: 'e.g. opendesktenant-controller',
      actorAriaLabel: 'Filter by actor',
      resource: 'Resource (kind)',
      resourcePlaceholder: 'e.g. OpenDeskTenant',
      resourceAriaLabel: 'Filter by resource type',
      tenant: 'Tenant (namespace)',
      tenantPlaceholder: 'e.g. opendesk-op-tenant',
      tenantAriaLabel: 'Filter by tenant',
      from: 'From',
      fromAriaLabel: 'Start time',
      to: 'To',
      toAriaLabel: 'End time',
      limit: 'Limit',
      limitAriaLabel: 'Maximum rows',
    },
    entries: {
      cardTitle: 'Audit entries',
      empty: 'No audit events in the selected time range. Operator actions are recorded in a format modelled on BSI OPS.1.1.5 and persisted via Kubernetes events.',
      loadMore: 'Load more',
      loading: 'Loading…',
      inNamespacePrefix: 'in',
      summarySuccess: 'success',
      summaryFailure: 'failure',
      summarySkipped: 'skipped',
    },
    table: {
      timestamp: 'Timestamp',
      actor: 'Actor',
      action: 'Action',
      resource: 'Resource',
      outcome: 'Outcome',
      framework: 'Framework',
      details: 'Details',
    },
    detail: {
      detail: 'Detail',
      bsiRef: 'BSI reference',
      frameworkRefs: 'Framework references',
    },
  },
} as const;
